// Tab-scoped, single-use store for the route an unauthenticated user was trying to reach.
// Uses sessionStorage deliberately: it survives the full page reload the 401 path does
// (window.location.replace("/")), survives the Auth0 round-trip (which returns the browser
// to window.location.origin, discarding any query param), is scoped to one tab, and is
// untouched by the several localStorage.clear() calls in the auth teardown paths.
//
// INVARIANT: resolve-once-per-page-load, single-use across page loads. The saved route (and,
// separately, the captured campaign params) are read from and removed from sessionStorage at
// most once per page load, and the resolved value - including null, if there was nothing to
// resolve - is memoized in module state for the rest of that page load. Every caller within the
// same page load therefore agrees on the same answer, no matter how many times or in what order
// they ask.
//
// This matters because more than one post-login navigator can be mounted, or re-fire, for the
// same login: handlePostLoginNavigation (keyed on userAdditionalInfo) and the Google/Microsoft
// SSO effects (which themselves dispatch a fresh UserAdditionalInfo on the employee branch,
// re-firing their own effect) can all ask "what was the saved route?" during one page load.
// Without memoization, whichever asks first gets the real answer and consumes it; everyone
// after gets null and silently overwrites the destination with the dashboard fallback. With
// memoization, every caller gets the same, consistent answer.
//
// Single-use is preserved across page loads because the sessionStorage entry is still removed
// on the first read of a page load - it just no longer needs re-reading afterward. Module state
// resets between logins because every path that ends a session forces a full page reload:
// Logout (store/reducers/Logout.js) does window.location.replace(...); the 401/refresh-failure
// paths in APIHelper.js/ApiUploadFile.js do window.location.replace("/"); and the Auth0 and
// Microsoft redirect round-trips both return via window.location.origin. There is no code path
// in this app that clears the session without also reloading the page.
import NavigationRoutes from "constants/NavigationRoutes";
import {
  LOGIN_ROUTE,
  LINK_EXPIRED_ROUTE,
  RESEND_LINK_ROUTE,
  EMPLOYEE_ONBOARDING_LINK_EXPIRED_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  RESET_PASSWORD_ROUTE,
  ADD_CUSTOMER_INFO_ROUTE,
  WAITLIST_ROUTE,
} from "constants/routes";

const STORAGE_KEY = "postLoginIntendedRoute";

// Exact public/auth paths from the top-level useRoutes table in src/routes/index.js. A saved
// route must never point back to one of these, or login would just bounce into itself.
const PUBLIC_ROUTES = new Set([
  LOGIN_ROUTE,
  "/admin",
  "/employee",
  "/changePassword",
  "/customerOnboarding/createPassword",
  LINK_EXPIRED_ROUTE,
  "/leaveStatus",
  NavigationRoutes.addPaymentMethod,
  RESEND_LINK_ROUTE,
  EMPLOYEE_ONBOARDING_LINK_EXPIRED_ROUTE,
  "/terms",
  "/policy",
  "/employee-policy",
  "/marketplace/public",
  FORGOT_PASSWORD_ROUTE,
  // Not currently wired into the route table (password reset is served at /changePassword on
  // this branch), but kept out of the store defensively since the constant names an auth screen.
  RESET_PASSWORD_ROUTE,
  ADD_CUSTOMER_INFO_ROUTE,
  // The public waitlist page at `/signup`. PUBLIC_ROUTE_PREFIXES carries "/signup/" WITH a
  // trailing slash for `/signup/:loginPortal`, which a bare `/signup` never matches, so it
  // has to be listed here explicitly — otherwise a visitor who lands on the waitlist and
  // then logs in is bounced straight back to it.
  WAITLIST_ROUTE,
]);

// "/signup/:loginPortal" takes a dynamic segment, so it is matched by prefix rather than exactly.
const PUBLIC_ROUTE_PREFIXES = ["/signup/"];

const isPublicRoute = (route: string): boolean =>
  PUBLIC_ROUTES.has(route) || PUBLIC_ROUTE_PREFIXES.some((prefix) => route.startsWith(prefix));

// Open-redirect guard: only a same-origin relative path is acceptable. Rejects protocol-relative
// ("//evil.com"), backslash-smuggled ("/\evil.com"), absolute ("https://evil.com"), and scheme
// payloads ("javascript:alert(1)") outright, since none of those start with exactly one "/".
export const isSafeInternalRoute = (route: string): boolean => {
  if (typeof route !== "string" || route.length === 0) return false;
  if (!route.startsWith("/")) return false;
  if (route.startsWith("//") || route.startsWith("/\\")) return false;
  return true;
};

export const saveIntendedRoute = (route: string): void => {
  try {
    if (!isSafeInternalRoute(route) || isPublicRoute(route)) return;
    sessionStorage.setItem(STORAGE_KEY, route);
  } catch {
    // Safari private mode (and similar) throws on storage access; never let this break login.
  }
};

// Module-level memo for the intended route. Resolved (read from sessionStorage, removed, and
// validated) at most once per page load; every call after the first - consume or peek - returns
// this same memoized value, including null.
let intendedRouteResolved = false;
let intendedRouteValue: string | null = null;

const readIntendedRouteFromStorage = (): string | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored && isSafeInternalRoute(stored) ? stored : null;
  } catch {
    return null;
  }
};

// Non-destructive, but memo-aware: once something has resolved (consumed) the route this page
// load, peek must return that same value rather than re-reading sessionStorage (which the
// resolution already emptied) - otherwise a caller downstream of the real consumer would
// disagree with it and see nothing where the consumer saw a route.
export const peekIntendedRoute = (): string | null => {
  if (intendedRouteResolved) return intendedRouteValue;
  return readIntendedRouteFromStorage();
};

export const consumeIntendedRoute = (): string | null => {
  if (!intendedRouteResolved) {
    intendedRouteValue = readIntendedRouteFromStorage();
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore - nothing left to clean up if storage is inaccessible
    }
    intendedRouteResolved = true;
  }
  return intendedRouteValue;
};

const CAMPAIGN_STORAGE_KEY = "postLoginCampaignParams";

const readCampaignParamsFromStorage = (): Record<string, string> | null => {
  try {
    const raw = sessionStorage.getItem(CAMPAIGN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Object.keys(parsed).length > 0 ? parsed : null;
  } catch {
    return null;
  }
};

// First-touch capture of campaign attribution off the tab's landing URL. Only the first
// successful capture is kept - a later navigation within the same tab (e.g. to /main/dashboard)
// must not overwrite the utm params the user actually arrived with.
export const captureCampaignParams = (search: string): void => {
  try {
    if (sessionStorage.getItem(CAMPAIGN_STORAGE_KEY) !== null) return;

    const campaignParams: Record<string, string> = {};
    new URLSearchParams(search).forEach((value, key) => {
      if (key.startsWith("utm_")) campaignParams[key] = value;
    });

    if (Object.keys(campaignParams).length === 0) return;
    sessionStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(campaignParams));
  } catch {
    // Safari private mode (and similar) throws on storage access; never let this break login.
  }
};

// Module-level memo for the captured campaign params - same resolve-once-per-page-load shape as
// the intended route above, and for the same reason: withCampaignParams is called by every
// post-login navigator, any of which may run more than once per login.
let campaignParamsResolved = false;
let campaignParamsValue: Record<string, string> | null = null;

// Test-only: resets the resolve-once-per-page-load memo so a test can start as if it were a
// fresh page load. Production code never calls this - there is no code path that "forgets" a
// resolution within a single page load (see the module invariant above); every real reset comes
// from a full page reload, which re-imports this module from scratch anyway.
// eslint-disable-next-line no-underscore-dangle -- conventional test-only marker
export const __resetForTests = (): void => {
  intendedRouteResolved = false;
  intendedRouteValue = null;
  campaignParamsResolved = false;
  campaignParamsValue = null;
};

const resolveCampaignParams = (): Record<string, string> | null => {
  if (!campaignParamsResolved) {
    campaignParamsValue = readCampaignParamsFromStorage();
    try {
      sessionStorage.removeItem(CAMPAIGN_STORAGE_KEY);
    } catch {
      // Safari private mode (and similar) throws on storage access; never let this break login.
    }
    campaignParamsResolved = true;
  }
  return campaignParamsValue;
};

// Non-destructive, memo-aware counterpart to resolveCampaignParams, mirroring peekIntendedRoute:
// once resolved this page load, agree with that resolution instead of re-reading storage.
const peekCampaignParams = (): Record<string, string> | null => {
  if (campaignParamsResolved) return campaignParamsValue;
  return readCampaignParamsFromStorage();
};

// Merges the captured campaign params onto route's query string, without overwriting a param
// route already carries under the same name. route is a relative path, not a full URL, so it is
// parsed by hand (hash, then query) rather than via `new URL()`. Resolving the campaign params is
// single-use across page loads (see module invariant above), but calling withCampaignParams
// itself is idempotent within a page load: the same route in always produces the same route out.
export const withCampaignParams = (route: string): string => {
  const stored = resolveCampaignParams();
  if (!stored) return route;

  const hashIndex = route.indexOf("#");
  const hash = hashIndex >= 0 ? route.slice(hashIndex) : "";
  const pathAndQuery = hashIndex >= 0 ? route.slice(0, hashIndex) : route;

  const queryIndex = pathAndQuery.indexOf("?");
  const path = queryIndex >= 0 ? pathAndQuery.slice(0, queryIndex) : pathAndQuery;
  const query = queryIndex >= 0 ? pathAndQuery.slice(queryIndex + 1) : "";

  const params = new URLSearchParams(query);
  Object.entries(stored).forEach(([key, value]) => {
    if (!params.has(key)) params.set(key, value);
  });

  const queryString = params.toString();
  return `${path}${queryString ? `?${queryString}` : ""}${hash}`;
};

// Standard "land the user" resolution for a login screen that navigates directly instead of
// going through handlePostLoginNavigation (e.g. the Google/Microsoft SSO effects): consume the
// saved route once, fall back to the given destination if there is none, and merge campaign
// params either way. Idempotent within a page load (see module invariant above), which is the
// point: these effects can and do re-fire for the same login.
export const resolvePostLoginRoute = (fallback: string): string =>
  withCampaignParams(consumeIntendedRoute() ?? fallback);

// For the enhanced-portal handoff (sender side): the saved route if one was captured, otherwise
// the current in-app path when it is itself a safe, non-public destination, otherwise - if
// campaign params were captured on this tab's landing URL - the enhanced portal's default
// landing path so campaign attribution still rides along. Peeks rather than consumes the
// intended route - the post-login navigator also consumes it, and the two can run in either
// order. Thanks to memoization this is no longer a race to get there first: whichever of the
// navigator or the handoff resolves first, the other sees the exact same memoized answer.
export const getHandoffNextRoute = (): string | null => {
  const savedRoute = peekIntendedRoute();
  if (savedRoute) return withCampaignParams(savedRoute);

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (isSafeInternalRoute(currentPath) && !isPublicRoute(currentPath)) {
    return withCampaignParams(currentPath);
  }

  if (peekCampaignParams()) {
    return withCampaignParams(NavigationRoutes.dashboard);
  }

  return null;
};
